import React, { useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import {
  App,
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Segmented,
  Switch,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";

import { apiCreateOptionStrategy } from "api/apiOptionStrategies";
import { paths } from "utils/constants";
import OptionStrategyFields, {
  OPTION_PARAM_DEFAULTS,
  pickParams,
} from "./OptionStrategyFields";

// "Create options strategy" button + modal, prefilled from an Options Analysis
// run (symbol, model, start date, target gain, min bars, excluded weekdays).
const CreateOptionStrategyModal = (props) => {
  const { symbol, model, startDate, analysisParams = {} } = props;
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const signalsOnly = Form.useWatch("signalsOnly", form);

  const minDays = analysisParams.minDays ?? OPTION_PARAM_DEFAULTS.minDays;
  const initialValues = {
    name: `${symbol} calls`,
    isDemo: true,
    signalsOnly: false,
    initialBalance: 10000,
    ...OPTION_PARAM_DEFAULTS,
    takeProfitPercent: analysisParams.targetGainPercent,
    minDays,
    maxDteBars: Math.max(
      analysisParams.maxDays ?? OPTION_PARAM_DEFAULTS.maxDteBars,
      minDays,
    ),
    excludedEntryWeekdays: analysisParams.excludedEntryWeekdays ?? [],
  };

  const onFinish = async (values) => {
    setIsSaving(true);
    try {
      await apiCreateOptionStrategy({
        name: values.name,
        symbol,
        models: [model._id],
        isDemo: values.isDemo,
        signalsOnly: values.signalsOnly,
        initialBalance: values.signalsOnly ? undefined : values.initialBalance,
        backtestStartDate: startDate,
        params: pickParams(values),
      });
      setIsOpen(false);
      message.success({
        content: (
          <span>
            Options strategy created.{" "}
            <Button
              type="link"
              size="small"
              onClick={() => navigate(`${paths.trading}?instrument=options`)}
            >
              Open
            </Button>
          </span>
        ),
      });
    } catch (err) {
      message.error(
        err?.response?.data?.message || "Failed to create options strategy",
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Button
        size="small"
        icon={<PlusOutlined />}
        onClick={() => setIsOpen(true)}
      >
        Create options strategy
      </Button>
      <Modal
        title={`New options strategy - ${symbol} · ${model?.name}`}
        open={isOpen}
        onCancel={() => setIsOpen(false)}
        onOk={() => form.submit()}
        okText="Create"
        confirmLoading={isSaving}
        width={800}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={initialValues}
          preserve={false}
        >
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="name"
                label="Strategy name"
                rules={[{ required: true, message: "Name is required" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col>
              <Form.Item name="isDemo" label="Account">
                <Segmented
                  options={[
                    { label: "Demo", value: true },
                    { label: "Real", value: false },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col>
              <Form.Item
                name="signalsOnly"
                label="Signals only"
                valuePropName="checked"
                tooltip="Track signals and get the email, without suggested trades or a balance"
              >
                <Switch />
              </Form.Item>
            </Col>
            {!signalsOnly && (
              <Col xs={24} sm={8}>
                <Form.Item
                  name="initialBalance"
                  label="Initial balance"
                  rules={[{ required: true, message: "Required" }]}
                >
                  <InputNumber min={1} prefix="$" style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            )}
          </Row>
          <OptionStrategyFields />
          <p style={{ color: "#999" }}>
            Every day at 15:50 ET: on a buy prediction it suggests the ATM call
            of the nearest expiry in the bars window (you accept or dismiss it),
            and suggests closing when the underlying reaches the take profit or
            on the option&apos;s expiration day.
          </p>
        </Form>
      </Modal>
    </>
  );
};

CreateOptionStrategyModal.propTypes = {
  symbol: PropTypes.string.isRequired,
  model: PropTypes.object.isRequired,
  startDate: PropTypes.string,
  analysisParams: PropTypes.object,
};

export default CreateOptionStrategyModal;
